import type { MarketplaceCategory } from '@/lib/marketplaceCatalog';

// ─────────────────────────────────────────────────────────────
// Barre de filtres des pages de listes marketplace :
// recherche par nom + filtre par ville + filtre par catégorie
// et sous-catégories (puces affichées sous la barre quand une
// catégorie racine est sélectionnée).
// ─────────────────────────────────────────────────────────────

interface MarketplaceFiltersProps {
  query: string;
  onQueryChange: (value: string) => void;
  city: string;
  onCityChange: (value: string) => void;
  cities: string[];
  category: string;
  onCategoryChange: (value: string) => void;
  categories: MarketplaceCategory[];
  subCategory?: string;
  onSubCategoryChange?: (value: string) => void;
  subCategories?: MarketplaceCategory[];
  placeholder?: string;
}

export default function MarketplaceFilters({
  query,
  onQueryChange,
  city,
  onCityChange,
  cities,
  category,
  onCategoryChange,
  categories,
  subCategory = '',
  onSubCategoryChange,
  subCategories = [],
  placeholder = 'Rechercher…',
}: MarketplaceFiltersProps) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-3 md:p-4">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_200px] gap-3">
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder={placeholder}
            className="w-full h-11 pl-9 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
          />
        </div>

        <div className="relative">
          <i className="ri-map-pin-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
          <select
            value={city}
            onChange={(e) => onCityChange(e.target.value)}
            className="w-full h-11 pl-9 pr-8 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
          >
            <option value="">Toutes les villes</option>
            {cities.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
        </div>

        <div className="relative">
          <i className="ri-apps-2-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm pointer-events-none"></i>
          <select
            value={category}
            onChange={(e) => onCategoryChange(e.target.value)}
            className="w-full h-11 pl-9 pr-8 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer appearance-none"
          >
            <option value="">Toutes les catégories</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.nom}</option>
            ))}
          </select>
          <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 pointer-events-none"></i>
        </div>
      </div>

      {/* Sous-catégories de la catégorie sélectionnée */}
      {subCategories.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-background-200/70">
          <span className="text-xs text-foreground-400 flex items-center gap-1">
            <i className="ri-corner-down-right-line"></i>
            Sous-catégories :
          </span>
          <button
            type="button"
            onClick={() => onSubCategoryChange?.('')}
            className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
              subCategory === ''
                ? 'bg-accent-500 text-background-50'
                : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
            }`}
          >
            Tout afficher
          </button>
          {subCategories.map((sub) => (
            <button
              key={sub.id}
              type="button"
              onClick={() => onSubCategoryChange?.(String(sub.id))}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                subCategory === String(sub.id)
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-accent-50 text-accent-700 hover:bg-accent-100'
              }`}
            >
              {sub.icon ? (
                <i className={`${sub.icon} text-sm`}></i>
              ) : sub.image_couverture ? (
                <span className={`w-5 h-5 rounded-full overflow-hidden flex-shrink-0 border ${subCategory === String(sub.id) ? 'border-white/40' : 'border-background-200/70'}`}>
                  <img src={sub.image_couverture} alt="" className="w-full h-full object-cover" />
                </span>
              ) : null}
              {sub.nom}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}